/**
 * 인증/동의 흐름 후 사용자가 원래 가려던 경로로 복귀시키기 위한 헬퍼.
 *
 * 메커니즘 분리:
 *  - 인증 단계: sessionStorage POST_LOGIN_REDIRECT_KEY (signIn callbackUrl 의 보조 fallback).
 *  - 동의 단계: URL query string `?next=<path>` (명시적·디버깅 가능, race/lag 없음).
 *    sessionStorage 기반은 보이지 않는 black box 라 NextAuth update() lag, SPA navigation
 *    transition 등에서 race 위험이 있어 명시적 query 로 대체한다.
 *
 * 보안:
 *  - {@link isSafeRedirectPath} 로 open redirect 차단 (절대 URL, protocol-relative,
 *    javascript:/data: 스킴 모두 거부). 모든 read 시점에서 검증.
 *  - sessionStorage 는 같은 origin 의 같은 탭 안에서만 접근 가능하므로 XSS 가
 *    없는 한 외부 조작 불가.
 */

import { signIn } from 'next-auth/react';

export const POST_LOGIN_REDIRECT_KEY = 'hai:auth:postLoginRedirect';

/**
 * Path 가 안전한 relative path 인지 검증.
 *
 * 차단 대상:
 *  - protocol-relative (`//`): 외부 사이트로 오픈 리디렉트
 *  - absolute URL (`http:`, `https:`)
 *  - javascript: / data: 스킴: XSS 위험
 *
 * 허용:
 *  - 단일 슬래시로 시작하는 path (`/dashboard`, `/templates/123?tab=meta` 등)
 */
export function isSafeRedirectPath(path: unknown): path is string {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false; // protocol-relative URL
  // javascript: / data: 는 슬래시로 시작 안 하므로 위에서 자동 차단됨.
  return true;
}

/**
 * /consent 가 무한 루프에 빠지지 않도록: next 파라미터로 /consent 자기 자신은 거부.
 * 동의 후 fallback path (`/`) 와 함께 사용.
 */
export function isSafeConsentNext(path: unknown): path is string {
  if (!isSafeRedirectPath(path)) return false;
  if (path === '/consent' || path.startsWith('/consent?') || path.startsWith('/consent/')) return false;
  return true;
}

/**
 * /consent 로 보낼 때 next 파라미터를 안전하게 인코딩한 URL 을 만든다.
 * @param next 동의 완료 후 복귀할 same-origin path (예: `/join/pl_xxx`).
 *             안전하지 않은 값이면 next 없는 `/consent` 만 반환.
 */
export function buildConsentUrl(next: string): string {
  if (!isSafeConsentNext(next)) return '/consent';
  return `/consent?next=${encodeURIComponent(next)}`;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** 미인증 가드가 발동할 때 호출: 인증 완료 transition 에서 복귀할 path 를 저장. */
export function savePostLoginRedirect(path: string): void {
  if (!isBrowser()) return;
  if (!isSafeRedirectPath(path)) return;
  // 랜딩/홈/기본 진입 경로 자체는 fallback 으로도 도달하므로 굳이 저장하지 않는다 (불필요한 stale 키 방지).
  if (path === '/' || path === '/dashboard' || path === '/fill-docs') return;
  window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
}

/** consume 하지 않고 현재 저장된 post-login path 만 읽는다 (signIn callbackUrl 결정용). */
export function peekPostLoginRedirect(): string | null {
  if (!isBrowser()) return null;
  const stored = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (!isSafeRedirectPath(stored)) return null;
  return stored;
}

/** 인증 완료 transition 에서 호출: read+remove. 안전하지 않은 값은 null + 키 제거. */
export function consumePostLoginRedirect(): string | null {
  if (!isBrowser()) return null;
  const stored = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  if (!isSafeRedirectPath(stored)) return null;
  return stored;
}

/**
 * 통합 signIn: NextAuth callbackUrl + sessionStorage 이중 보존.
 *
 * Primary: NextAuth `callbackUrl` 옵션 (state·cookie 로 보존, 정상 케이스에서 직접 복귀).
 * Fallback: sessionStorage POST_LOGIN_REDIRECT_KEY: Keycloak required action
 *   (UPDATE_PASSWORD 등) 으로 OAuth flow 시간이 길어져 NextAuth state·cookie 가
 *   expire 되면 callbackUrl 이 / 로 떨어진다. 이때 AuthProvider/PostLoginGate 가
 *   sessionStorage 키를 consume → 원래 가려던 returnPath 로 router.replace.
 *
 * cleanup 은 AuthProvider 의 인증 transition effect 가 항상 consume 하므로
 * 호출처에서 별도 처리 불필요. savePostLoginRedirect 는 `/`·`/dashboard` 자동 제외 +
 * 안전성 검증을 하므로 부작용 없음.
 */
export function signInWithReturn(
  provider: 'keycloak' | 'keycloak-register',
  returnPath: string,
): void {
  savePostLoginRedirect(returnPath);
  signIn(provider, { callbackUrl: returnPath });
}
